import { useEffect, useRef, useState } from "react";
import { Settings, RefreshCw, Loader2 } from "lucide-react";

// Source-agnostic player: youtube | mp4 | hls | iframe.
// `source` = { type, youtubeId, embed, url, hls, iframe }
export const MediaPlayer = ({ source, title = "", autoplay = true, testid = "media-player", onUnavailable, onRecovered }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const nativeRef = useRef(null);
  // Keep the latest callbacks in a ref so the HLS effect never re-runs (and thus never
  // tears down/rebuilds the player) just because the parent re-rendered with new inline fns.
  const cbRef = useRef({ onUnavailable, onRecovered });
  cbRef.current = { onUnavailable, onRecovered };
  const [levels, setLevels] = useState([]); // [{index, height, bitrate}]
  const [currentLevel, setCurrentLevel] = useState(-1); // -1 = Auto
  const [menuOpen, setMenuOpen] = useState(false);
  const [buffering, setBuffering] = useState(true);
  const [stalled, setStalled] = useState(false); // shown when no parent fallback is provided
  const [attempt, setAttempt] = useState(0);

  const type = source?.type;
  const hlsUrl = source?.hls || (type === "hls" ? source?.url : null);
  const fileUrl = type === "mp4" ? source?.url : null;

  // Playback watchdog: if a stream/file hasn't actually started within 25s, never leave an
  // endless spinner — tell the parent (offline/retry overlay) or show our own Retry button.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !(hlsUrl || fileUrl)) return undefined;
    setBuffering(true); setStalled(false);
    if (attempt > 0 && fileUrl) video.load();
    let started = false;
    const fail = () => {
      if (started) return;
      if (cbRef.current.onUnavailable) cbRef.current.onUnavailable(); else setStalled(true);
    };
    const onPlaying = () => { started = true; setBuffering(false); setStalled(false); cbRef.current.onRecovered && cbRef.current.onRecovered(); };
    const onWaiting = () => setBuffering(true);
    const onCanPlay = () => {
      setBuffering(false);
      // Browsers block un-muted autoplay: fall back to muted autoplay instead of sitting at 0:00.
      if (autoplay && video.paused) video.play().catch(() => { video.muted = true; video.play().catch(() => {}); });
    };
    const onPause = () => setBuffering(false);
    const onErr = () => { if (!hlsRef.current) fail(); };
    video.addEventListener("playing", onPlaying);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("canplay", onCanPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("error", onErr);
    const t = setTimeout(() => { if (!started && video.readyState < 3) fail(); }, 25000);
    return () => {
      clearTimeout(t);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("canplay", onCanPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("error", onErr);
    };
  }, [hlsUrl, fileUrl, attempt, autoplay]);

  useEffect(() => {
    setLevels([]); setCurrentLevel(-1); setMenuOpen(false);
    nativeRef.current = null;
    if (!hlsUrl || !videoRef.current) return;
    const video = videoRef.current;
    let dead = false;
    // Native HLS only when hls.js can't run (older iPhones). Quality menu still works by switching to a variant playlist.
    const native = async () => {
      video.src = hlsUrl;
      try {
        const txt = await (await fetch(hlsUrl)).text();
        const lines = txt.split(/\r?\n/);
        const vs = [];
        lines.forEach((l, i) => {
          const m = l.startsWith("#EXT-X-STREAM-INF") && l.match(/RESOLUTION=\d+x(\d+)/);
          const uri = m && (lines[i + 1] || "").trim();
          if (m && uri && !uri.startsWith("#")) vs.push({ height: Number(m[1]), url: new URL(uri, hlsUrl).href });
        });
        const uniq = [...new Map(vs.map((v) => [v.height, v])).values()].sort((a, b) => b.height - a.height);
        if (dead) return;
        nativeRef.current = { master: hlsUrl, variants: uniq };
        setLevels(uniq.map((v, i) => ({ index: i, height: v.height })));
      } catch (_) { /* quality list unavailable — playback continues on Auto */ }
    };
    // Chrome/Firefox: load hls.js from CDN on demand
    let hls;
    let fatalCount = 0;
    let downTimer = null;      // grace period before declaring the stream offline
    let recoverTimer = null;
    const clearTimers = () => { if (downTimer) clearTimeout(downTimer); if (recoverTimer) clearTimeout(recoverTimer); downTimer = null; recoverTimer = null; };
    const setup = () => {
      // eslint-disable-next-line no-undef
      if (window.Hls && window.Hls.isSupported()) {
        // eslint-disable-next-line no-undef
        const Hls = window.Hls;
        // Production-tuned: retry manifest/level/fragment loads with timeouts before giving up.
        hls = new Hls({
          manifestLoadingTimeOut: 12000, manifestLoadingMaxRetry: 6, manifestLoadingRetryDelay: 1000,
          levelLoadingTimeOut: 12000, levelLoadingMaxRetry: 6, levelLoadingRetryDelay: 1000,
          fragLoadingTimeOut: 20000, fragLoadingMaxRetry: 8, fragLoadingRetryDelay: 1000,
          maxBufferLength: 30, liveSyncDurationCount: 3, lowLatencyMode: false,
        });
        hlsRef.current = hls;
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          fatalCount = 0; clearTimers();
          cbRef.current.onRecovered && cbRef.current.onRecovered();
          const seen = new Set();
          const lv = (hls.levels || [])
            .map((l, i) => ({ index: i, height: l.height, bitrate: l.bitrate }))
            .filter((l) => l.height).sort((a, b) => b.height - a.height || b.bitrate - a.bitrate)
            .filter((l) => (seen.has(l.height) ? false : seen.add(l.height)));
          setLevels(lv);
          setCurrentLevel(hls.autoLevelEnabled ? -1 : hls.currentLevel);
          video.play().catch(() => {});
        });
        hls.on(Hls.Events.FRAG_LOADED, () => { fatalCount = 0; clearTimers(); cbRef.current.onRecovered && cbRef.current.onRecovered(); });
        hls.on(Hls.Events.LEVEL_SWITCHED, (e, d) => setCurrentLevel(hls.autoLevelEnabled ? -1 : d.level));
        hls.on(Hls.Events.ERROR, (e, data) => {
          if (!data.fatal) return;
          // Attempt in-place recovery first (transient network/media hiccups).
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            recoverTimer = setTimeout(() => { try { hls.startLoad(); } catch (_) {} }, Math.min(1000 * (fatalCount + 1), 6000));
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            try { hls.recoverMediaError(); } catch (_) {}
          }
          fatalCount += 1;
          // Grace period: only declare "offline" if we can't recover within ~15s of persistent fatals.
          if (fatalCount >= 3 && !downTimer) {
            downTimer = setTimeout(() => { cbRef.current.onUnavailable && cbRef.current.onUnavailable(); }, 15000);
          }
        });
      } else {
        native();
      }
    };
    if (window.Hls) setup();
    else {
      const s = document.createElement("script");
      s.src = "https://cdn.jsdelivr.net/npm/hls.js@1.5.13/dist/hls.min.js";
      s.onload = setup;
      s.onerror = native; // CDN blocked: try the browser's own player
      document.body.appendChild(s);
    }
    return () => { dead = true; clearTimers(); if (hls) hls.destroy(); hlsRef.current = null; };
  }, [hlsUrl, attempt]);

  const pick = (idx) => {
    const hls = hlsRef.current;
    const nat = nativeRef.current;
    if (hls) hls.currentLevel = idx; // -1 => auto
    else if (nat && videoRef.current) {
      const v = videoRef.current;
      v.src = idx === -1 ? nat.master : nat.variants[idx].url;
      v.play().catch(() => {});
    }
    setCurrentLevel(idx);
    setMenuOpen(false);
  };
  const labelFor = (idx) => {
    if (idx === -1) return "Auto";
    const l = levels.find((x) => x.index === idx);
    return l ? `${l.height}p` : "Auto";
  };

  if (!source) {
    return <div className="aspect-video flex items-center justify-center text-slate-500 bg-black">Video unavailable</div>;
  }

  if (type === "youtube" && (source.embed || source.youtubeId)) {
    // Prefer the server-provided hardened no-cookie embed; never build a plain youtube.com link.
    let src = source.embed || `https://www.youtube-nocookie.com/embed/${source.youtubeId}?rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&fs=1&disablekb=1`;
    if (autoplay) src += (src.includes("?") ? "&" : "?") + "autoplay=1";
    return (
      <div className="aspect-video bg-black">
        <iframe title={title} data-testid={testid} src={src} className="w-full h-full"
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />
      </div>
    );
  }

  if (type === "iframe" && source.url) {
    return (
      <div className="aspect-video bg-black">
        <iframe title={title} data-testid={testid} src={source.url} className="w-full h-full"
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowFullScreen />
      </div>
    );
  }

  if (hlsUrl || fileUrl) {
    return (
      <div className="aspect-video bg-black relative">
        <video ref={videoRef} data-testid={testid} src={fileUrl || undefined} controls
          autoPlay={autoplay} playsInline className="w-full h-full" aria-label={title || "Video player"} />
        {buffering && !stalled && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid={`${testid}-buffering`}>
            <Loader2 className="w-10 h-10 text-white/80 animate-spin" aria-label="Loading video" />
          </div>
        )}
        {stalled && (
          <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center text-center p-6" data-testid={`${testid}-stalled`}>
            <div className="text-white font-bold">This video isn't starting right now.</div>
            <p className="text-slate-300 text-sm mt-1">The stream may be offline or your connection is slow.</p>
            <button onClick={() => setAttempt((a) => a + 1)} data-testid={`${testid}-retry`}
              className="mt-4 h-10 px-5 rounded-full bg-[#E11D2A] text-white font-bold inline-flex items-center gap-2">
              <RefreshCw className="w-4 h-4" aria-hidden="true" /> Retry
            </button>
          </div>
        )}
        {levels.length > 1 && (
          <div className="absolute top-3 right-3 z-10">
            <button onClick={() => setMenuOpen((o) => !o)} data-testid="quality-btn"
              className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-black/70 backdrop-blur text-white text-xs font-semibold border border-white/10 hover:bg-black/90 transition-colors">
              <Settings className="w-3.5 h-3.5" /> {labelFor(currentLevel)}
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-1.5 w-32 rounded-lg bg-[#0A0C10] border border-[#273042] overflow-hidden shadow-xl" data-testid="quality-menu">
                <button onClick={() => pick(-1)} data-testid="quality-auto"
                  className={`w-full text-left px-3 py-2 text-xs transition-colors hover:bg-white/5 ${currentLevel === -1 ? "text-[#E11D2A] font-bold" : "text-slate-200"}`}>Auto</button>
                {levels.map((l) => (
                  <button key={l.index} onClick={() => pick(l.index)} data-testid={`quality-${l.height}`}
                    className={`w-full text-left px-3 py-2 text-xs transition-colors hover:bg-white/5 ${currentLevel === l.index ? "text-[#E11D2A] font-bold" : "text-slate-200"}`}>{l.height}p</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return <div className="aspect-video flex items-center justify-center text-slate-500 bg-black">Video unavailable</div>;
};
